import React, { useState, useCallback, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { head } from 'lodash';

import { useCreateModal } from 'partial/components/Modal';
import { usePersistState, removeEmpty, aclHelper } from 'helper';

import List from 'partial/components/List';
import Pagination from 'partial/components/Pagination';
import ThumbIcon from 'partial/components/ThumbIcon';
import AsyncButton from 'modules/common/components/AsyncButton';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import AddUpdateUser from './modals/AddUpdateUser';
import UserDetails from './container/UserDetails';
import StatusFilter from './components/StatusFilter';

import { useUsers } from './hooks';

const UserManagement = () => {
  const { setSubLabel } = useModuleContext();

  const navigate = useNavigate();
  const createModal = useCreateModal();
  const [filter, setFilter] = useState({ q: '', is_active: '' });
  const [selected, setSelected] = usePersistState('user-uuid', '', {
    keepOnUnmount: false,
  });
  const [isLoading, users, meta] = useUsers(removeEmpty(filter));

  const id = head(users)?.uuid;

  const onSubSelect = (item) => {
    setSelected(item?.uuid);
    navigate(`${item?.uuid}`);
  };

  const handleAddUser = useCallback(() => {
    createModal({
      content: (onClose) => <AddUpdateUser onClose={onClose} />,
    });
  }, [createModal]);

  useEffect(() => {
    if (!id) return;
    setSelected(id);
  }, [id, setSelected]);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} user${meta?.total === 1 ? '' : 's'}`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  return (
    <section className="min-h-[calc(100vh_-_12rem)] flex p-6 bg-white drop-shadow rounded-lg flex-grow">
      <div className="flex flex-col gap-3 pr-4 border-r border-gray-50 min-w-[260px]">
        <StatusFilter
          name="q"
          value={filter?.q}
          onChange={setFilter}
          inputClassName="py-[1.05rem] !w-full"
        />

        <List
          data={users}
          isLoading={isLoading}
          selectedId={selected}
          onSelectRow={onSubSelect}
          containerClassName="!w-full"
          render={(item) => {
            const fullName = `${item?.profile?.first_name} ${item?.profile?.last_name}`;
            return (
              <div className="flex items-center gap-2 pl-2 py-1 w-full ">
                <ThumbIcon className="h-8 w-8 rounded-full" alt={fullName} />
                <div className="flex flex-col text-sm">
                  <span className="capitalize">{fullName}</span>
                  <span className="lowercase break-all">{item?.email}</span>
                </div>
              </div>
            );
          }}
        />
        <div className="mt-auto">
          <div className="flex justify-center mb-3">
            <Pagination onChange={setFilter} value={meta} withPage={false} />
          </div>
          {aclHelper(
            'user',
            'w',
            <AsyncButton className="w-full" onClick={handleAddUser}>
              add user
            </AsyncButton>,
          )}
        </div>
      </div>

      <Routes>
        <Route path=":id/*" element={<UserDetails users={users} />} />
        {users?.length > 0 && (
          <Route
            index
            element={<Navigate replace to={`${head(users)?.uuid}`} />}
          />
        )}
      </Routes>
    </section>
  );
};

export default UserManagement;
